import { action, computed, makeObservable, observable, override } from 'mobx'

import ModalViewModel from 'infra/ModalViewModel'
import { format } from 'date-fns'
import { Nullable } from 'types/definitions'
import sortBy from 'lodash/sortBy'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import { TimeRange } from 'jobs/TimeRange'
import { task } from 'mobx-task'
import { DeferredPromise } from 'infra/deferred'
import Consultation from '../../../../../jobs/consultations/Consultation'
import Member from '../../../../../iam/models/Member'
import { JobType } from '../../../../dtos/job'

/**
 * A day and its timeslots for which the pro can book a consultation
 * based on the client's availability.
 */
interface ScheduledDay {
  formattedDay: string
  timeSlots: TimeRange[]
}

/**
 * Information used for creating a consultation and prompting the user.
 */
export interface ConsultationDetails {
  availability: () => TimeRange[]
  bookedConsultations: () => Consultation[]
  requestedLength: () => number
  clientMember: Member
  currentMember: Member
}

/**
 * Consultation details.
 */
const ConsultationDetails: { none: ConsultationDetails } = {
  /**
   * An empty details "instance".
   */
  none: {
    requestedLength: () => 30,
    availability: () => [],
    bookedConsultations: () => [],
    clientMember: Member.none,
    currentMember: Member.none,
  },
}

/**
 * Result of a consultation booking operation.
 */
export abstract class ConsultationPendingBookingResult {
  /**
   * Book the selected time slot.
   */
  static Book = class extends ConsultationPendingBookingResult {
    constructor(public selectedTimeSlot: TimeRange) {
      super()
    }
  }

  /**
   * Forfeit the consultation.
   */
  static Forfeit = class extends ConsultationPendingBookingResult {
    constructor() {
      super()
    }
  }
}

/**
 * View Model for the Consultation Modal.
 */
export default class ConsultationPendingBookingModalViewModel extends ModalViewModel {
  /**
   * The active consultation.
   */
  private _activeConsultationDetails: ConsultationDetails =
    ConsultationDetails.none

  /**
   * The time slot selected by the pro. This is one of the time slots
   * allowable based on the customer's specified availability.
   */
  @observable
  selectedTimeSlot: Nullable<TimeRange> = null

  /**
   * The days and the time slots within them for which the pro can book
   * the consultation based on the client's availability.
   */
  @observable
  availableDays: ScheduledDay[] = []

  @observable
  private _jobType?: JobType = undefined

  /**
   * The provider's selection. This promise can be awaited by whichever caller
   * inflated the modal to receive the pro's selected time slot.
   */
  deferredSelectedTimeSlot =
    new DeferredPromise<ConsultationPendingBookingResult>()

  /**
   * Constructor.
   */
  constructor() {
    super()

    makeObservable(this)
  }

  /**
   * The customer's requested length in minutes for the Consultation.
   */
  @computed
  get requestedLength() {
    return this._activeConsultationDetails.requestedLength()
  }

  /**
   * The customer's availability for the Consultation.
   */
  @computed
  get availability() {
    return this._activeConsultationDetails.availability()
  }

  /**
   * List of booked consultations.
   */
  @computed
  get bookedConsultations() {
    return this._activeConsultationDetails.bookedConsultations()
  }

  /**
   * The client member.
   */
  @computed
  get clientMember() {
    return this._activeConsultationDetails.clientMember
  }

  /**
   * The current member.
   */
  @computed
  get currentMember() {
    return this._activeConsultationDetails.currentMember
  }

  /**
   * Indicates whether the consultation is for a tax job.
   */
  @computed
  get consultationIsForTaxJob() {
    return this._jobType?.type === 'TAX'
  }

  /**
   * Inflates the Modal.
   */
  @action
  promptForTimeSlots(
    consultationDetails: ConsultationDetails,
    jobType?: JobType,
  ) {
    this.deferredSelectedTimeSlot.reset()
    this._activeConsultationDetails = consultationDetails
    this._jobType = jobType

    this.show()
    this.availableDays = []
    this.availableDays = this.computeScheduledDays(this.availability)

    return this.deferredSelectedTimeSlot.promise
  }

  /**
   * @inheritDoc
   */
  @override
  close() {
    this.deferredSelectedTimeSlot.resolve(null)
    this.showing = false
  }

  /**
   * Selects the time slot.
   */
  @action
  selectTimeSlot(range: TimeRange) {
    this.selectedTimeSlot = range
  }

  /**
   * Given the customer's availability,
   */
  private computeScheduledDays(ranges: TimeRange[]) {
    const sorted = sortBy(ranges, 'startTime')
    const grouped = groupBy(sorted, (t) => format(t.startTime, 'EEEE, d MMMM'))

    return map(grouped, (timeSlots, formattedDay) => {
      return { formattedDay, timeSlots }
    })
  }

  /**
   * Book the selected time slot.
   */
  book = task.resolved(() => {
    if (!this.selectedTimeSlot) {
      return
    }

    this.deferredSelectedTimeSlot.resolve(
      new ConsultationPendingBookingResult.Book(this.selectedTimeSlot),
    )
    this.close()
  })

  /**
   * Forfeit the consultation.
   */
  forfeit = task.resolved(() => {
    this.deferredSelectedTimeSlot.resolve(
      new ConsultationPendingBookingResult.Forfeit(),
    )
    this.close()
  })
}
